import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { monthPhase, currentMonthKey } from '../../lib/months';
import { useTrend } from '../../hooks/useTrend';
import { emptyMonth } from '../../lib/trend';
import { fmtInt } from '../../lib/format';

const STORY_COLOR = '#2e7d32';
const BUG_COLOR   = '#c62828';

type Kind = 'created' | 'resolved';

type Props = { months: string[]; kind: Kind };

// Zeile für Created bzw. Resolved Counts pro Monat. Stories und Bugs werden
// nebeneinander dargestellt; rechts steht die Σ-Spalte mit Aufschlüsselung.
export function TrendRow({ months, kind }: Props) {
  const { index, available, totals } = useTrend();
  const cur = currentMonthKey();
  const has = kind === 'created' ? available.created : available.resolved;
  const label = kind === 'created' ? '📂 Created' : '✅ Resolved';
  const accent = kind === 'created' ? 'warning.main' : 'success.main';

  return (
    <Box
      role="row"
      sx={{
        display: 'grid',
        gridTemplateColumns: `200px repeat(${months.length}, minmax(60px, 1fr)) 70px`,
        borderBottom: 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ p: 1, borderRight: 1, borderColor: 'divider' }}>
        <Typography variant="overline" sx={{ color: accent, fontWeight: 700, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: STORY_COLOR, mr: 0.5 }} />
          Stories
          <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: BUG_COLOR, ml: 1, mr: 0.5 }} />
          Bugs
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', fontSize: 11 }}>
          <b>K</b>=Kunden-Bugs · <b>I</b>=intern
        </Typography>
        {!has && (
          <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 600 }}>
            Spalte „{kind === 'created' ? 'Created' : 'Resolved'}" fehlt
          </Typography>
        )}
      </Box>

      {months.map(mk => {
        const phase = monthPhase(mk, cur);
        const v = has ? (index.get(mk) ?? emptyMonth()) : emptyMonth();
        const s = kind === 'created' ? v.openedStories : v.closedStories;
        const b = kind === 'created' ? v.openedBugs    : v.closedBugs;
        const bCust = kind === 'created' ? v.openedCustomerBugs : v.closedCustomerBugs;
        const bInt  = kind === 'created' ? v.openedInternalBugs : v.closedInternalBugs;
        return (
          <Box
            key={mk}
            sx={{
              position: 'relative',
              p: 0.5,
              borderLeft: 1,
              borderColor: 'divider',
              bgcolor: phase === 'now' ? 'action.selected' : 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.15,
              minHeight: 52,
            }}
          >
            {has ? (
              <>
                <Typography variant="caption" sx={{ color: STORY_COLOR, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {s}
                </Typography>
                <Typography variant="caption" sx={{ color: BUG_COLOR, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {b}
                </Typography>
                {b > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1, fontSize: 11, opacity: 0.75 }}
                    title={`Kunden-Bugs: ${bCust} · interne Bugs: ${bInt}`}
                  >
                    K{bCust}·I{bInt}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.disabled">–</Typography>
            )}
          </Box>
        );
      })}

      <Box sx={{ borderLeft: 1, borderColor: 'divider', p: 0.75, textAlign: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {has ? fmtInt(kind === 'created' ? totals.openedStories + totals.openedBugs : totals.closedStories + totals.closedBugs) : '–'}
        </Typography>
        {has && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
              S {kind === 'created' ? totals.openedStories : totals.closedStories} · B {kind === 'created' ? totals.openedBugs : totals.closedBugs}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', lineHeight: 1.2, fontSize: 11, opacity: 0.75 }}>
              K{kind === 'created' ? totals.openedCustomerBugs : totals.closedCustomerBugs}·I{kind === 'created' ? totals.openedInternalBugs : totals.closedInternalBugs}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
